import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Save, Loader2, Plus, X } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const IndustriesTab = ({ headers }) => {
  const [industries, setIndustries] = useState([]);
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploadingImg, setUploadingImg] = useState(false);
  const [uploadingGallery, setUploadingGallery] = useState(false);
  const selectedRef = useRef(null);

  useEffect(() => { load(); }, []);

  const load = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/industries?all=true`);
      setIndustries(res.data || []);
      const keep = selectedRef.current || res.data?.[0]?.slug;
      const cur = (res.data || []).find(i => i.slug === keep);
      if (cur) selectIndustry(cur);
    } catch (e) {
      toast.error('Failed to load industries');
    }
  };

  const move = async (dir) => {
    const idx = industries.findIndex(i => i.slug === selected);
    const j = idx + dir;
    if (idx < 0 || j < 0 || j >= industries.length) return;
    const a = industries[idx], b = industries[j];
    await axios.put(`${API_URL}/api/industries/${a.slug}`, { order: b.order ?? j }, { headers });
    await axios.put(`${API_URL}/api/industries/${b.slug}`, { order: a.order ?? idx }, { headers });
    await load();
    toast.success('Order updated');
  };

  const toggleActive = async () => {
    await axios.put(`${API_URL}/api/industries/${form.slug}`, { is_active: !form.is_active }, { headers });
    setForm(f => ({ ...f, is_active: !f.is_active }));
    await load();
  };

  const selectIndustry = (ind) => {
    setSelected(ind.slug);
    selectedRef.current = ind.slug;
    setForm({
      ...ind,
      why: (ind.why || []).join('\n'),
      faqs: (ind.faqs && ind.faqs.length) ? ind.faqs.map(f => ({ q: f.q || '', a: f.a || '' })) : [],
      gallery: (ind.gallery || []).map(g => typeof g === 'string' ? { url: g, caption: '' } : { url: g.url || '', caption: g.caption || '' }),
      case_study: {
        quote: ind.case_study?.quote || '',
        author: ind.case_study?.author || '',
        stats: [0, 1, 2].map(i => ind.case_study?.stats?.[i] || { v: '', l: '' })
      }
    });
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        eyebrow: form.eyebrow,
        title: form.title,
        lead: form.lead,
        hero_image: form.hero_image,
        segment: form.segment,
        recommend: form.recommend,
        video_url: form.video_url || '',
        why: form.why.split('\n').map(s => s.trim()).filter(Boolean),
        faqs: (form.faqs || []).filter(f => (f.q || '').trim() && (f.a || '').trim()),
        gallery: (form.gallery || []).filter(g => (g.url || '').trim()).map(g => ({ url: g.url.trim(), caption: (g.caption || '').trim() })),
        case_study: {
          quote: form.case_study.quote,
          author: form.case_study.author,
          stats: form.case_study.stats.filter(s => s.v || s.l)
        }
      };
      await axios.put(`${API_URL}/api/industries/${form.slug}`, payload, { headers });
      toast.success('Industry saved');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const uploadImage = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingImg(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await axios.post(`${API_URL}/api/industries/upload-image`, fd, { headers: { ...headers, 'Content-Type': 'multipart/form-data' } });
      setForm(f => ({ ...f, hero_image: res.data.file_url }));
      toast.success('Image uploaded');
    } catch (err) {
      toast.error('Image upload failed');
    } finally {
      setUploadingImg(false);
    }
  };

  const setStat = (i, key, val) => {
    const stats = [...form.case_study.stats];
    stats[i] = { ...stats[i], [key]: val };
    setForm({ ...form, case_study: { ...form.case_study, stats } });
  };

  const addFaq = () => setForm(f => ({ ...f, faqs: [...(f.faqs || []), { q: '', a: '' }] }));
  const setFaq = (i, key, val) => setForm(f => { const faqs = [...(f.faqs || [])]; faqs[i] = { ...faqs[i], [key]: val }; return { ...f, faqs }; });
  const removeFaq = (i) => setForm(f => ({ ...f, faqs: (f.faqs || []).filter((_, idx) => idx !== i) }));

  const setGalleryItem = (i, key, val) => setForm(f => { const g = [...(f.gallery || [])]; g[i] = { ...g[i], [key]: val }; return { ...f, gallery: g }; });
  const removeGalleryItem = (i) => setForm(f => ({ ...f, gallery: (f.gallery || []).filter((_, idx) => idx !== i) }));
  const addGalleryItem = () => setForm(f => ({ ...f, gallery: [...(f.gallery || []), { url: '', caption: '' }] }));
  const uploadGalleryImage = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingGallery(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await axios.post(`${API_URL}/api/industries/upload-image`, fd, { headers: { ...headers, 'Content-Type': 'multipart/form-data' } });
      setForm(f => ({ ...f, gallery: [...(f.gallery || []), { url: res.data.file_url, caption: '' }] }));
      toast.success('Image added to gallery');
    } catch (err) {
      toast.error('Gallery upload failed');
    } finally {
      setUploadingGallery(false);
    }
  };

  return (
    <div data-testid="industries-tab">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-[#0a0203]">Industry Pages</h2>
        <p className="text-gray-500">Edit the case studies, stats and content shown on each industry landing page.</p>
      </div>

      <div className="flex flex-wrap gap-2 mb-6">
        {industries.map(ind => (
          <button key={ind.slug} onClick={() => selectIndustry(ind)}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${selected === ind.slug ? 'bg-[#003456] text-white' : 'bg-white border border-gray-200 text-gray-700 hover:border-[#003456]'}`}
            data-testid={`industry-select-${ind.slug}`}>
            {ind.eyebrow}
          </button>
        ))}
      </div>

      {form && (
        <div className="flex flex-wrap items-center gap-2 mb-6" data-testid="ind-toolbar">
          <button onClick={() => move(-1)} className="px-3 py-1.5 bg-white border rounded-lg text-sm hover:border-[#003456]" data-testid="ind-move-up">↑ Move up</button>
          <button onClick={() => move(1)} className="px-3 py-1.5 bg-white border rounded-lg text-sm hover:border-[#003456]" data-testid="ind-move-down">↓ Move down</button>
          <button onClick={toggleActive} className={`px-3 py-1.5 rounded-lg text-sm font-medium ${form.is_active ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-200 text-gray-600'}`} data-testid="ind-toggle-active">
            {form.is_active ? '● Visible on homepage' : '○ Hidden'}
          </button>
        </div>
      )}

      {form && (
        <div className="bg-white rounded-2xl p-6 space-y-5 max-w-3xl">
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Eyebrow</label>
              <input value={form.eyebrow} onChange={(e) => setForm({ ...form, eyebrow: e.target.value })} className="w-full p-2.5 border rounded-lg" data-testid="ind-eyebrow" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Recommended model</label>
              <input value={form.recommend} onChange={(e) => setForm({ ...form, recommend: e.target.value })} className="w-full p-2.5 border rounded-lg" data-testid="ind-recommend" />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
            <input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="w-full p-2.5 border rounded-lg" data-testid="ind-title" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Lead / intro</label>
            <textarea rows={2} value={form.lead} onChange={(e) => setForm({ ...form, lead: e.target.value })} className="w-full p-2.5 border rounded-lg resize-none" data-testid="ind-lead" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Hero image</label>
            <input value={form.hero_image} onChange={(e) => setForm({ ...form, hero_image: e.target.value })} className="w-full p-2.5 border rounded-lg mb-2" data-testid="ind-hero" placeholder="Image URL" />
            <div className="flex items-center gap-3">
              <label className="inline-flex items-center gap-2 px-4 py-2 bg-[#F8FAFC] text-[#003456] rounded-lg text-sm font-medium cursor-pointer hover:bg-[#d5f2f3]" data-testid="ind-upload-label">
                {uploadingImg ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                {uploadingImg ? 'Uploading...' : 'Upload image'}
                <input type="file" accept="image/*" className="hidden" onChange={uploadImage} data-testid="ind-upload-input" disabled={uploadingImg} />
              </label>
              {form.hero_image && <img src={form.hero_image} alt="hero preview" className="h-12 w-20 object-cover rounded-md border" />}
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">"Why" points (one per line)</label>
            <textarea rows={4} value={form.why} onChange={(e) => setForm({ ...form, why: e.target.value })} className="w-full p-2.5 border rounded-lg resize-none" data-testid="ind-why" />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Video embed URL (YouTube / Vimeo)</label>
            <input value={form.video_url || ''} onChange={(e) => setForm({ ...form, video_url: e.target.value })} className="w-full p-2.5 border rounded-lg" data-testid="ind-video" placeholder="e.g. https://www.youtube.com/embed/XXXX — leave blank to show a placeholder" />
            <p className="text-xs text-gray-400 mt-1">Use the embed URL. Leave blank to display a "video coming soon" placeholder.</p>
          </div>

          <div className="border-t border-gray-100 pt-5">
            <h3 className="font-semibold text-[#0a0203] mb-3">Case Study</h3>
            <label className="block text-sm font-medium text-gray-700 mb-1">Quote</label>
            <textarea rows={3} value={form.case_study.quote} onChange={(e) => setForm({ ...form, case_study: { ...form.case_study, quote: e.target.value } })} className="w-full p-2.5 border rounded-lg resize-none mb-3" data-testid="ind-quote" />
            <label className="block text-sm font-medium text-gray-700 mb-1">Attribution</label>
            <input value={form.case_study.author} onChange={(e) => setForm({ ...form, case_study: { ...form.case_study, author: e.target.value } })} className="w-full p-2.5 border rounded-lg mb-4" data-testid="ind-author" />
            <label className="block text-sm font-medium text-gray-700 mb-2">Stats (value + label)</label>
            <div className="space-y-2">
              {form.case_study.stats.map((s, i) => (
                <div key={i} className="flex gap-2">
                  <input value={s.v} onChange={(e) => setStat(i, 'v', e.target.value)} placeholder="e.g. 3x" className="w-32 p-2.5 border rounded-lg" data-testid={`ind-stat-v-${i}`} />
                  <input value={s.l} onChange={(e) => setStat(i, 'l', e.target.value)} placeholder="e.g. output increase" className="flex-1 p-2.5 border rounded-lg" data-testid={`ind-stat-l-${i}`} />
                </div>
              ))}
            </div>
          </div>

          <div className="border-t border-gray-100 pt-5">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-semibold text-[#0a0203]">FAQs</h3>
              <button type="button" onClick={addFaq} className="inline-flex items-center gap-1 text-sm text-[#003456] font-medium hover:underline" data-testid="ind-faq-add"><Plus className="h-4 w-4" /> Add FAQ</button>
            </div>
            <p className="text-xs text-gray-400 mb-3">Leave empty to show the default freeze-drying FAQ set on the page.</p>
            <div className="space-y-3">
              {(form.faqs || []).map((f, i) => (
                <div key={i} className="border border-gray-100 rounded-lg p-3 bg-gray-50/50" data-testid={`ind-faq-row-${i}`}>
                  <div className="flex items-start gap-2">
                    <div className="flex-1 space-y-2">
                      <input value={f.q} onChange={(e) => setFaq(i, 'q', e.target.value)} placeholder="Question" className="w-full p-2.5 border rounded-lg" data-testid={`ind-faq-q-${i}`} />
                      <textarea rows={2} value={f.a} onChange={(e) => setFaq(i, 'a', e.target.value)} placeholder="Answer" className="w-full p-2.5 border rounded-lg resize-none" data-testid={`ind-faq-a-${i}`} />
                    </div>
                    <button type="button" onClick={() => removeFaq(i)} className="p-2 text-gray-400 hover:text-red-500" data-testid={`ind-faq-remove-${i}`}><X className="h-4 w-4" /></button>
                  </div>
                </div>
              ))}
              {(form.faqs || []).length === 0 && <p className="text-sm text-gray-400 italic">No custom FAQs — default set is shown.</p>}
            </div>
          </div>

          <div className="border-t border-gray-100 pt-5">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-semibold text-[#0a0203]">Gallery photos</h3>
              <div className="flex items-center gap-3">
                <button type="button" onClick={addGalleryItem} className="inline-flex items-center gap-1 text-sm text-[#003456] font-medium hover:underline" data-testid="ind-gallery-add"><Plus className="h-4 w-4" /> Add URL</button>
                <label className="inline-flex items-center gap-1 text-sm text-[#003456] font-medium cursor-pointer hover:underline" data-testid="ind-gallery-upload-label">
                  {uploadingGallery ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />} Upload photo
                  <input type="file" accept="image/*" className="hidden" onChange={uploadGalleryImage} data-testid="ind-gallery-upload-input" disabled={uploadingGallery} />
                </label>
              </div>
            </div>
            <p className="text-xs text-gray-400 mb-3">Add an optional caption to each photo to tell the story. Leave the whole gallery empty to show the default results gallery.</p>
            <div className="space-y-3">
              {(form.gallery || []).map((item, i) => (
                <div key={i} className="flex items-start gap-2 border border-gray-100 rounded-lg p-3 bg-gray-50/50" data-testid={`ind-gallery-row-${i}`}>
                  {item.url && <img src={item.url} alt="" className="h-14 w-16 object-cover rounded-md border flex-shrink-0" />}
                  <div className="flex-1 space-y-2">
                    <input value={item.url} onChange={(e) => setGalleryItem(i, 'url', e.target.value)} placeholder="Image URL" className="w-full p-2.5 border rounded-lg" data-testid={`ind-gallery-input-${i}`} />
                    <input value={item.caption} onChange={(e) => setGalleryItem(i, 'caption', e.target.value)} placeholder="Caption (optional)" className="w-full p-2.5 border rounded-lg" data-testid={`ind-gallery-caption-${i}`} />
                  </div>
                  <button type="button" onClick={() => removeGalleryItem(i)} className="p-2 text-gray-400 hover:text-red-500" data-testid={`ind-gallery-remove-${i}`}><X className="h-4 w-4" /></button>
                </div>
              ))}
              {(form.gallery || []).length === 0 && <p className="text-sm text-gray-400 italic">No custom photos — default gallery is shown.</p>}
            </div>
          </div>

          <button onClick={save} disabled={saving} className="inline-flex items-center gap-2 bg-[#003456] text-white font-semibold px-6 py-3 rounded-md hover:bg-[#002B47] transition-colors disabled:opacity-60" data-testid="ind-save">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      )}
    </div>
  );
};

export default IndustriesTab;
